/**
 * API タイムアウト設定ユーティリティ
 * 非機能要件のパフォーマンス要件（API応答5秒以内、配車計算30秒以内）を実現
 */

import { performanceMonitor } from './performanceMonitor';
import logger from './logger';

export interface TimeoutConfig {
  timeout: number; // ミリ秒
  retryAttempts?: number;
  retryDelay?: number; // ミリ秒
}

export interface TimeoutOptions {
  name?: string;
  metadata?: Record<string, any>;
  onTimeout?: () => void;
  onRetry?: (attempt: number) => void;
}

// 非機能要件に基づく標準タイムアウト設定
export const TimeoutConfigs = {
  // API応答: 5秒以内
  API_STANDARD: {
    timeout: 5000,
    retryAttempts: 3,
    retryDelay: 1000,
  },

  // 配車計算: 30秒以内
  DISPATCH_CALCULATION: {
    timeout: 30000,
    retryAttempts: 2,
    retryDelay: 2000,
  },

  // リアルタイム通知: 1秒以内
  REALTIME_NOTIFICATION: {
    timeout: 1000,
    retryAttempts: 3,
    retryDelay: 500,
  },

  // 軽微なAPI: 2秒以内
  FAST_API: {
    timeout: 2000,
    retryAttempts: 2,
    retryDelay: 500,
  },
} as const;

export class TimeoutError extends Error {
  constructor(
    message: string,
    public readonly timeoutMs: number,
    public readonly operationName?: string
  ) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Promise にタイムアウトを適用
 */
export function withTimeout<T>(
  promise: Promise<T>,
  config: TimeoutConfig,
  options: TimeoutOptions = {}
): Promise<T> {
  const { timeout, retryAttempts = 0, retryDelay = 1000 } = config;
  const { name = 'unknown-operation', metadata, onTimeout, onRetry } = options;

  return new Promise<T>((resolve, reject) => {
    let isResolved = false;
    let attempts = 0;

    const executeWithTimeout = async (): Promise<void> => {
      attempts++;

      // パフォーマンス監視開始
      if (attempts === 1) {
        performanceMonitor.startMeasure(name, {
          timeout,
          attempt: attempts,
          ...metadata,
        });
      }

      const timeoutId = setTimeout(() => {
        if (!isResolved) {
          isResolved = true;

          // タイムアウトコールバック実行
          if (onTimeout) {
            onTimeout();
          }

          const error = new TimeoutError(
            `Operation "${name}" timed out after ${timeout}ms (attempt ${attempts}/${retryAttempts + 1})`,
            timeout,
            name
          );

          // リトライ可能な場合
          if (attempts <= retryAttempts) {
            if (onRetry) {
              onRetry(attempts);
            }

            logger.warn(
              `⏰ Timeout: "${name}" (${timeout}ms), retrying in ${retryDelay}ms... (${attempts}/${retryAttempts + 1})`
            );

            setTimeout(() => {
              executeWithTimeout();
            }, retryDelay);
            return;
          }

          // 最終的な失敗
          performanceMonitor.endMeasure(name);
          logger.error(`❌ Final timeout: "${name}" after ${attempts} attempts`);
          reject(error);
        }
      }, timeout);

      try {
        const result = await promise;

        if (!isResolved) {
          isResolved = true;
          clearTimeout(timeoutId);

          // 成功時のパフォーマンス監視終了
          if (attempts === 1) {
            performanceMonitor.endMeasure(name);
          }

          resolve(result);
        }
      } catch (error) {
        if (!isResolved) {
          clearTimeout(timeoutId);

          // エラー時のリトライ判定
          if (attempts <= retryAttempts) {
            if (onRetry) {
              onRetry(attempts);
            }

            logger.warn(
              `🔄 Error occurred, retrying "${name}" in ${retryDelay}ms... (${attempts}/${retryAttempts + 1})`,
              error
            );

            setTimeout(() => {
              executeWithTimeout();
            }, retryDelay);
            return;
          }

          // 最終的なエラー
          isResolved = true;
          performanceMonitor.endMeasure(name);
          reject(error);
        }
      }
    };

    executeWithTimeout();
  });
}

/**
 * API呼び出し用のタイムアウトラッパー
 */
export async function apiWithTimeout<T>(
  apiCall: () => Promise<T>,
  operationName: string,
  config: TimeoutConfig = TimeoutConfigs.API_STANDARD,
  options: Omit<TimeoutOptions, 'name'> = {}
): Promise<T> {
  return withTimeout(apiCall(), config, {
    name: operationName,
    metadata: { type: 'api' },
    ...options,
  });
}

/**
 * 配車計算専用のタイムアウトラッパー
 */
export async function dispatchWithTimeout<T>(
  calculationFn: () => Promise<T>,
  metadata?: Record<string, any>
): Promise<T> {
  return withTimeout(calculationFn(), TimeoutConfigs.DISPATCH_CALCULATION, {
    name: 'dispatch-calculation',
    metadata: { type: 'dispatch', ...metadata },
    onTimeout: () => {
      logger.warn('🚨 Dispatch calculation is taking longer than expected (30s)');
    },
    onRetry: attempt => {
      logger.info(`Retrying dispatch calculation (attempt ${attempt})`);
    },
  });
}

/**
 * 通知送信専用のタイムアウトラッパー
 */
export async function notificationWithTimeout<T>(
  notificationFn: () => Promise<T>,
  metadata?: Record<string, any>
): Promise<T> {
  return withTimeout(notificationFn(), TimeoutConfigs.REALTIME_NOTIFICATION, {
    name: 'realtime-notification',
    metadata: { type: 'notification', ...metadata },
    onTimeout: () => {
      logger.warn('🚨 Notification is taking longer than expected (1s)');
    },
  });
}

/**
 * フェッチAPIのタイムアウト対応ラッパー
 */
export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  config: TimeoutConfig = TimeoutConfigs.API_STANDARD
): Promise<Response> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), config.timeout);

  try {
    const response = await fetch(input, {
      ...init,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.name === 'AbortError') {
      throw new TimeoutError(
        `Fetch request timed out after ${config.timeout}ms`,
        config.timeout,
        'fetch-request'
      );
    }

    throw error;
  }
}

/**
 * 複数のPromiseに対するタイムアウト制御
 */
export async function allWithTimeout<T>(
  promises: Promise<T>[],
  config: TimeoutConfig,
  operationName: string = 'parallel-operations'
): Promise<T[]> {
  return withTimeout(Promise.all(promises), config, {
    name: operationName,
    metadata: {
      type: 'parallel',
      count: promises.length,
    },
  });
}

/**
 * レース条件でのタイムアウト制御
 */
export async function raceWithTimeout<T>(
  promises: Promise<T>[],
  config: TimeoutConfig,
  operationName: string = 'race-operations'
): Promise<T> {
  return withTimeout(Promise.race(promises), config, {
    name: operationName,
    metadata: {
      type: 'race',
      count: promises.length,
    },
  });
}

/**
 * デバウンス機能付きタイムアウト制御
 */
export function debounceWithTimeout<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  debounceMs: number,
  config: TimeoutConfig,
  operationName: string = 'debounced-operation'
): T {
  let timeoutId: NodeJS.Timeout | null = null;

  return ((...args: Parameters<T>) => {
    return new Promise<Awaited<ReturnType<T>>>((resolve, reject) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(async () => {
        try {
          const result = await withTimeout(fn(...args), config, { name: operationName });
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, debounceMs);
    });
  }) as T;
}
